import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Banner, Button, Card, Field, Input, Select } from "@magarac/ui";
import { describeRefundPolicy } from "@magarac/shared/money";
import type { SettingsDeposits } from "@magarac/shared/schemas/settings";
import { HelpIcon } from "../../../components/HelpIcon";
import { messageFor } from "../../../lib/errors";
import { useSetting } from "../../../lib/settings";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/settings/payments")({
  component: PaymentsSettingsPage,
});

/** CRM-21 deposit refund policy; BIL-9 Stripe connection (Phase 4c). Screen key: settings.payments */
function PaymentsSettingsPage() {
  const { active } = useTenant();
  const { query, save, value } = useSetting("deposits");
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Payments</h1>
        <HelpIcon screen="settings.payments" />
      </div>
      <Card title="Take card payments" className="mb-6">
        <p className="text-muted">
          Connecting your Stripe account lets customers pay deposits and balances by card from a
          link we text or email them. This arrives shortly; until then you can record cash and check
          payments on each job.
        </p>
      </Card>
      {query.isPending ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <DepositPolicyForm
          key={active?.id ?? "none"}
          initial={value ?? { refundPolicy: "non_refundable", refundableUntilHours: 48 }}
          onSave={(v) => save.mutateAsync(v)}
          busy={save.isPending}
          error={save.error ? messageFor(save.error) : null}
          saved={save.isSuccess}
        />
      )}
    </div>
  );
}

function DepositPolicyForm({
  initial,
  onSave,
  busy,
  error,
  saved,
}: {
  initial: SettingsDeposits;
  onSave: (v: SettingsDeposits) => Promise<unknown>;
  busy: boolean;
  error: string | null;
  saved: boolean;
}) {
  const [form, setForm] = useState<SettingsDeposits>(initial);
  return (
    <Card title="Deposit refunds">
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {saved ? <Banner tone="ok">Saved.</Banner> : null}
      <Field
        id="refund-policy"
        label="Refund policy"
        hint="Customers see this on the payment page before they pay."
      >
        <Select
          id="refund-policy"
          value={form.refundPolicy}
          onChange={(e) =>
            setForm({ ...form, refundPolicy: e.target.value as SettingsDeposits["refundPolicy"] })
          }
        >
          <option value="non_refundable">Non-refundable</option>
          <option value="refundable">Fully refundable</option>
          <option value="refundable_until">Refundable until shortly before the visit</option>
        </Select>
      </Field>
      {form.refundPolicy === "refundable_until" ? (
        <Field id="refund-hours" label="Hours before the visit">
          <Input
            id="refund-hours"
            type="number"
            min={1}
            max={720}
            value={form.refundableUntilHours}
            onChange={(e) =>
              setForm({ ...form, refundableUntilHours: Math.max(1, Number(e.target.value) || 1) })
            }
          />
        </Field>
      ) : null}
      <p className="mb-3 text-sm text-muted">Customers will read: “{describeRefundPolicy(form)}”</p>
      <Button busy={busy} onClick={() => void onSave(form)}>
        Save
      </Button>
    </Card>
  );
}
